/**
 * Access control rule for a tool.
 *
 * Rules are evaluated with OR logic: if *any* rule passes, access is granted.
 * Omitting `access` entirely means the tool is public (no restrictions).
 *
 * Supported rule types:
 * - `appAdmin` — user must be an app-level admin (`user.isAdmin`)
 * - `featureFlag` — user must have the specified feature flag enabled
 * - `authenticated` — user must be signed in (any role)
 *
 * This is designed to be extended with future rule types (e.g. `role`, `entityAdmin`, `plan`).
 */
export type ToolAccessRule =
  | { type: 'appAdmin' }
  | { type: 'featureFlag'; flag: string }
  | { type: 'authenticated' };

export interface SizzleTool {
  title: string;
  description: string;
  href: string;
  /** Lucide icon name */
  icon?: 'ClipboardCheck' | 'FileBarChart' | 'Wrench';
  tags: string[];
  disabled?: boolean;
  /** Link to the original source material or page this tool is based on */
  sourceUrl?: string;
  /** Label for the source link (defaults to "View original source") */
  sourceLabel?: string;
  /**
   * Access rules controlling visibility and access.
   * If omitted or empty array, the tool is publicly accessible.
   * Multiple rules are OR'd — user needs to satisfy at least one.
   */
  access?: ToolAccessRule[];
}

export const TOOLS: SizzleTool[] = [
  {
    title: "Why Aren't People Buying Your Product?",
    description:
      "Work out why people aren't buying your product — whether they simply don't know about it, have seen it but not considered it, or have no reason not to buy but just aren't — and get ideas to change that.",
    href: '/tools/why-not-buying',
    tags: ['Brands', 'Awareness', 'Trial', 'Self-Assessment'],
    disabled: false,
  },
  {
    title: 'Do People Need To Try Before They Will Buy Assessment',
    description:
      'Sampling and tastings cost real money per person reached. Work out whether lack of experience is genuinely what holds purchase back for your product, what a first experience would have to prove, and which formats could land it.',
    href: '/tools/try-before-buy-assessment',
    tags: ['Brands', 'Sampling & Trial', 'Awareness', 'Self-Assessment'],
    disabled: false,
    access: [{ type: 'appAdmin' }],
  },
  {
    title: 'Product Tasting Planner',
    description:
      'Model what a tasting costs, what happens if a chosen percentage of tasters buy, and the taste-to-purchase conversion required to break even.',
    href: '/tools/product-tasting-planner',
    tags: ['Brands', 'Sampling & Trial', 'ROI', 'Activation Planning'],
    disabled: false,
  },
  {
    title: 'NZ Export Reality Check',
    description:
      "A pressure-test for NZ food & beverage and consumer good brands considering export. Walk the common reasons it doesn't work out, flag the ones that are real for you, and submit your results for follow-up.",
    href: '/tools/export-reality-check',
    tags: ['Export', 'Growth Strategy', 'Self-Assessment'],
    disabled: false,
  },
  {
    title: 'Should We Export? Decision & Readiness Assessment',
    description:
      'Challenge whether export is the right growth investment, assess execution readiness, identify stop signals, and leave with prioritised actions and reusable assessment data.',
    href: '/export/export-should-readiness',
    tags: ['Export', 'Growth Strategy', 'Self-Assessment'],
    disabled: true,
  },
  {
    title: 'Retail Readiness Assessment',
    description:
      "A step-by-step wizard that helps brands evaluate whether they're ready for retail — covering product, business, market, channel, and operations.",
    href: '/tools/retail-readiness',
    // icon: 'ClipboardCheck',
    tags: ['Brands', 'Retail Entry', 'Self-Assessment'],
    disabled: true,
  },
  {
    title: 'GLOWPATH Australian Retail Readiness Assessment',
    description:
      'Interactive walkthrough of the GLOWPATH 2026 Australian Retail Readiness Snapshot and a self assessment. Learn what buyers evaluate, why brands get declined, and assess your brand against real buyer data.',
    href: '/tools/au-retail-readiness-report',
    // icon: 'FileBarChart',
    tags: ['Export', 'Australia', 'Retail Buyer Insights', 'Retail Entry'],
    sourceUrl: 'https://drive.google.com/file/d/1t0hiL2SMfRb8zSZPR4GV57hdarfiy_eN/view',
    sourceLabel: 'View original GLOWPATH Australian Retail Readiness Snapshot',
  },
];

/**
 * Context passed to canAccessTool for evaluating access rules.
 * Mirrors the shape available from the frontend session.
 */
export interface ToolAccessContext {
  /** Whether the user is signed in */
  isAuthenticated: boolean;
  /** Whether the user is an app-level admin */
  isAdmin?: boolean;
  /** User's feature flags (from session user.data.flags) */
  featureFlags?: Record<string, boolean | string | number | undefined>;
}

/**
 * Check whether a user can access a given tool based on its access rules.
 *
 * - If the tool has no `access` array (or it's empty), it's public — returns true.
 * - Otherwise, returns true if the user satisfies at least one rule (OR logic).
 */
export function canAccessTool(tool: SizzleTool, ctx: ToolAccessContext): boolean {
  // No access rules = public tool
  if (!tool.access || tool.access.length === 0) {
    return true;
  }

  return tool.access.some((rule) => {
    switch (rule.type) {
      case 'appAdmin':
        return ctx.isAdmin === true;
      case 'authenticated':
        return ctx.isAuthenticated;
      case 'featureFlag':
        return Boolean(ctx.featureFlags?.[rule.flag]);
      default:
        // Unknown rule types fail closed (deny access)
        return false;
    }
  });
}
